import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import {
  accountantFormSchema,
  SHARE_MODE_LABEL,
  type AccountantFormInput,
  type AccountantShareMode,
} from '@/features/accountant/schemas';

interface Props {
  initial?: Partial<AccountantFormInput>;
  submitLabel: string;
  isSubmitting?: boolean;
  onSubmit: (values: AccountantFormInput) => Promise<void> | void;
  onCancel?: () => void;
}

const MODES: AccountantShareMode[] = ['AUTO_MONTHLY_PACKAGE', 'READONLY_LINK', 'BOTH'];

const defaults: AccountantFormInput = {
  name: '',
  email: '',
  shareMode: 'AUTO_MONTHLY_PACKAGE',
  monthlyPackageDay: 5,
};

export function AccountantForm({ initial, submitLabel, isSubmitting, onSubmit, onCancel }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AccountantFormInput>({
    resolver: zodResolver(accountantFormSchema),
    defaultValues: { ...defaults, ...initial },
  });

  useEffect(() => {
    reset({ ...defaults, ...initial });
  }, [initial, reset]);

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await onSubmit(values);
      })}
      className="flex flex-col gap-4"
    >
      <FormField id="accountant-name" label="Nome" error={errors.name?.message} required>
        <Input id="accountant-name" {...register('name')} invalid={!!errors.name} />
      </FormField>

      <FormField id="accountant-email" label="E-mail" error={errors.email?.message} required>
        <Input
          id="accountant-email"
          type="email"
          {...register('email')}
          invalid={!!errors.email}
        />
      </FormField>

      <FormField
        id="accountant-share-mode"
        label="Modo de compartilhamento"
        error={errors.shareMode?.message}
        required
      >
        <select
          id="accountant-share-mode"
          {...register('shareMode')}
          className="h-11 w-full rounded-md border border-(--color-border) bg-(--color-surface) px-3 text-sm text-(--color-fg) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)"
        >
          {MODES.map((m) => (
            <option key={m} value={m}>
              {SHARE_MODE_LABEL[m]}
            </option>
          ))}
        </select>
      </FormField>

      <FormField
        id="accountant-monthly-day"
        label="Dia do envio automático"
        hint="Dia do mês (1–28) em que o pacote é gerado e enviado."
        error={errors.monthlyPackageDay?.message}
        required
      >
        <Input
          id="accountant-monthly-day"
          type="number"
          min={1}
          max={28}
          {...register('monthlyPackageDay', { valueAsNumber: true })}
          invalid={!!errors.monthlyPackageDay}
        />
      </FormField>

      <div className="flex flex-wrap justify-end gap-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
        <Button type="submit" disabled={isSubmitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
